import { useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { Redirect } from 'platform/react-router';

import { getHubSellingRoute } from 'shared/helpers/routes';
import { useObservability } from 'shared/helpers/observability';

import { LoadingPage } from 'platform/components/LoadingPage';

import { useExperiments } from 'sellDirect/helpers/ExperimentProvider';

import { useUpdateHomeConditionResponse } from 'sellDirect/routes/price-adjustment/graphql/useUpdateHomeConditionResponse';
import { ProxyImagesWizard } from 'sellDirect/routes/price-adjustment/components/ProxyImagesWizard';
import { useQualificationContext } from 'shared/helpers/QualificationContext';

// 5 seconds to wait
const MIN_MS_TO_WAIT = 5_000;

/**
 * Renders UI for the Home Condition flow for the Price Adjustment V2 experiment
 * https://opendoor.atlassian.net/wiki/spaces/CONSUMER/pages/2135195708/Price+Adjustment+Version+2
 * component-test-check-ignore storybook-check-ignore
 */
export const HomeConditionPage: React.FC = () => {
  const { loading, updateHomeConditionResponse } = useUpdateHomeConditionResponse();
  const history = useHistory();
  const { addressUUID } = useQualificationContext();
  const { trackEvent, trackError } = useObservability();
  const { priceAdjustment } = useExperiments();
  const [submitting, setSubmitting] = useState(false);

  /** Callback to submit the responses. We force this to take a min of 5 seconds */
  const submitHomeConditionResponse = async (selectedIds: Array<string>) => {
    // start "timer"
    const start = Date.now();
    setSubmitting(true);
    trackEvent('home-conditions-flow', 'flow-submitted');

    // submit responses
    const hasMlsPhotos = !!priceAdjustment.mlsPhotos?.length;
    await updateHomeConditionResponse(selectedIds, hasMlsPhotos);

    // end "timer"
    const end = Date.now();
    const totalMsElapsed = end - start;
    const additionalWaitTime =
      totalMsElapsed > MIN_MS_TO_WAIT ? 0 : MIN_MS_TO_WAIT - totalMsElapsed;
    await new Promise((resolve) => setTimeout(resolve, additionalWaitTime));

    // refetch and redirect
    priceAdjustment.refetch && (await priceAdjustment.refetch());
    history.replace(generatePath(getHubSellingRoute(), { addressUUID }));
  };

  if (loading || submitting) {
    return <LoadingPage from="ProxyImagesWizard" reason="loading" />;
  }

  if (!priceAdjustment.priceAdjustmentResponse?.homeConditionOptions) {
    trackError('home-conditions-flow--missing-options');
    <Redirect to={generatePath(getHubSellingRoute(), { addressUUID })} />;
  }

  if (priceAdjustment.priceAdjustmentResponse?.homeConditionOptions) {
    return (
      <ProxyImagesWizard
        mlsPhotos={priceAdjustment.mlsPhotos}
        homeConditions={priceAdjustment.priceAdjustmentResponse?.homeConditionOptions}
        onComplete={(selectedIds) => submitHomeConditionResponse(selectedIds)}
      />
    );
  }
  return null;
};
